export interface JobMetrics {
  active: number;
  waiting: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: number;
}

export interface DocumentJobMetrics {
  totalDocuments: number;
  documentsProcessingToday: number;
  avgProcessingTimeMs: number;
  successRate: number;
  failureRate: number;
  mostCommonFailure?: string;
  activeDocumentJobs: number;
  completedDocumentJobs: number;
  failedDocumentJobs: number;
}

// 🚀 NEW: MCP Cost Tracking Interface (Phase 3)
export interface McpCostMetrics {
  totalAnalysisCost: number;
  analysesPerformed: number;
  avgAnalysisCost: number;
  costToday: number;
  costThisMonth: number;
  mostExpensiveAnalysis?: {
    jobId: string;
    cost: number;
    timestamp: string;
  };
}

interface JobMetricsPanelProps {
  metrics: JobMetrics | null;
  documentMetrics?: DocumentJobMetrics | null;
  lastUpdated: Date | null;
  showDocumentMetrics?: boolean;
  // 🚀 NEW: MCP cost tracking (Phase 3)
  mcpCostMetrics?: McpCostMetrics | null;
  showMcpCosts?: boolean;
}

export default function JobMetricsPanel({ 
  metrics, 
  documentMetrics, 
  lastUpdated, 
  showDocumentMetrics = false,
  // 🚀 NEW: MCP cost tracking (Phase 3)
  mcpCostMetrics = null,
  showMcpCosts = false
}: JobMetricsPanelProps) {
  if (!metrics && !documentMetrics && !mcpCostMetrics) return null;

  const metricCardClass =
    'flex flex-col items-center justify-center p-4 rounded-2xl bg-gray-100 dark:bg-gray-800 shadow text-center min-w-[110px]';

  const documentCardClass = 
    'flex flex-col items-center justify-center p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 shadow text-center min-w-[110px] border border-green-200 dark:border-green-800';

  // 🚀 NEW: MCP cost card styling (Phase 3)
  const mcpCostCardClass = 
    'flex flex-col items-center justify-center p-4 rounded-2xl bg-purple-50 dark:bg-purple-900/20 shadow text-center min-w-[110px] border border-purple-200 dark:border-purple-800';

  const labelClass = 'text-sm text-gray-600 dark:text-gray-400';
  const countClass = 'text-2xl font-bold text-gray-900 dark:text-white';
  const documentLabelClass = 'text-sm text-green-700 dark:text-green-300';
  const documentCountClass = 'text-2xl font-bold text-green-800 dark:text-green-200';
  
  // 🚀 NEW: MCP cost styling (Phase 3)
  const mcpCostLabelClass = 'text-sm text-purple-700 dark:text-purple-300';
  const mcpCostCountClass = 'text-2xl font-bold text-purple-800 dark:text-purple-200';

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.round(seconds / 60);
    return `${minutes}m`;
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  // 🚀 NEW: Cost formatting utility (Phase 3)
  const formatCost = (cost: number) => {
    if (cost < 0.01) return `$${(cost * 1000).toFixed(2)}k`;
    return `$${cost.toFixed(3)}`;
  };

  return (
    <div className="mb-6 space-y-4">
      {/* General Job Metrics */}
      {metrics && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <span>📊</span>
            <span>Queue Metrics</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            <div className={metricCardClass}>
              <span className={countClass}>{metrics.active}</span>
              <span className={labelClass}>Active</span>
            </div>
            <div className={metricCardClass}>
              <span className={countClass}>{metrics.waiting}</span>
              <span className={labelClass}>Waiting</span>
            </div>
            <div className={metricCardClass}>
              <span className={countClass}>{metrics.completed}</span>
              <span className={labelClass}>Completed</span>
            </div>
            <div className={metricCardClass}>
              <span className={countClass}>{metrics.failed}</span>
              <span className={labelClass}>Failed</span>
            </div>
            <div className={metricCardClass}>
              <span className={countClass}>{metrics.delayed}</span>
              <span className={labelClass}>Delayed</span>
            </div>
            <div className={metricCardClass}>
              <span className={countClass}>{metrics.paused}</span>
              <span className={labelClass}>Paused</span>
            </div>
          </div>
        </div>
      )}

      {/* Document Processing Metrics */}
      {showDocumentMetrics && documentMetrics && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <span>📄</span>
            <span>Document Processing Metrics</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className={documentCardClass}>
              <span className={documentCountClass}>{documentMetrics.totalDocuments}</span>
              <span className={documentLabelClass}>Total Docs</span>
            </div>
            <div className={documentCardClass}>
              <span className={documentCountClass}>{documentMetrics.documentsProcessingToday}</span>
              <span className={documentLabelClass}>Today</span>
            </div>
            <div className={documentCardClass}>
              <span className={documentCountClass}>
                {formatDuration(documentMetrics.avgProcessingTimeMs)}
              </span>
              <span className={documentLabelClass}>Avg Time</span>
            </div>
            <div className={documentCardClass}>
              <span className={documentCountClass}>
                {formatPercentage(documentMetrics.successRate)}
              </span>
              <span className={documentLabelClass}>Success Rate</span>
            </div>
            <div className={documentCardClass}>
              <span className={documentCountClass}>{documentMetrics.activeDocumentJobs}</span>
              <span className={documentLabelClass}>Active</span>
            </div>
            <div className={documentCardClass}>
              <span className={documentCountClass}>{documentMetrics.failedDocumentJobs}</span>
              <span className={documentLabelClass}>Failed</span>
            </div>
          </div>
          
          {/* Additional Document Insights */}
          {documentMetrics.mostCommonFailure && (
            <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <span className="font-semibold">Most Common Failure:</span> {documentMetrics.mostCommonFailure}
              </p>
            </div>
          )}
        </div>
      )}

      {/* 🚀 NEW: MCP Analysis Cost Tracking (Phase 3) */}
      {showMcpCosts && mcpCostMetrics && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <span>🧠</span>
            <span>AI Analysis Costs</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            <div className={mcpCostCardClass}>
              <span className={mcpCostCountClass}>{formatCost(mcpCostMetrics.totalAnalysisCost)}</span>
              <span className={mcpCostLabelClass}>Total Cost</span>
            </div>
            <div className={mcpCostCardClass}>
              <span className={mcpCostCountClass}>{mcpCostMetrics.analysesPerformed}</span>
              <span className={mcpCostLabelClass}>Analyses</span>
            </div>
            <div className={mcpCostCardClass}>
              <span className={mcpCostCountClass}>{formatCost(mcpCostMetrics.avgAnalysisCost)}</span>
              <span className={mcpCostLabelClass}>Avg Cost</span>
            </div>
            <div className={mcpCostCardClass}>
              <span className={mcpCostCountClass}>{formatCost(mcpCostMetrics.costToday)}</span>
              <span className={mcpCostLabelClass}>Today</span>
            </div>
            <div className={mcpCostCardClass}>
              <span className={mcpCostCountClass}>{formatCost(mcpCostMetrics.costThisMonth)}</span>
              <span className={mcpCostLabelClass}>This Month</span>
            </div>
          </div>
          
          {/* Most Expensive Analysis Alert */}
          {mcpCostMetrics.mostExpensiveAnalysis && mcpCostMetrics.mostExpensiveAnalysis.cost > 0.05 && (
            <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <span className="font-semibold">🚨 High Cost Alert:</span> Most expensive analysis: {formatCost(mcpCostMetrics.mostExpensiveAnalysis.cost)} 
                <span className="ml-2 font-mono text-xs">
                  (Job: {mcpCostMetrics.mostExpensiveAnalysis.jobId.slice(0, 8)}...)
                </span>
              </p>
            </div>
          )}
        </div>
      )}

      {lastUpdated && (
        <p className="text-xs text-right text-gray-500 dark:text-gray-400">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </p>
      )}
    </div>
  );
}
